import type { BBox, BezierPath, VectorBoxParams, VectorFillData, VectorOrigination, VectorStrokeData } from './custom-template';

/**
 * Скаляры без оси (обводка, радиусы, blur/size, feather) едут √(xScale·yScale): только оно перемножается
 * на ресайзах подряд, симметрично по осям и даёт k на ×k. Масштаб ≤0/NaN/∞ — коэффициент 1: √ от него не сохранить.
 * distance тени и глянца сюда не входит: это длина ориентированного вектора, её масштабирует scaleShadowGeometry.
 */
export function uniformEffectScale(xScale: number, yScale: number): number {
  if (!isPositiveScale(xScale) || !isPositiveScale(yScale)) return 1;
  return Math.sqrt(xScale * yScale);
}

function scaleBBox(b: BBox, xScale: number, yScale: number, offsetX = 0, offsetY = 0): BBox {
  return {
    x: b.x * xScale + offsetX,
    y: b.y * yScale + offsetY,
    w: b.w * xScale,
    h: b.h * yScale,
  };
}

export function scaleOrigination(
  o: VectorOrigination,
  xScale: number,
  yScale: number,
  offsetX: number,
  offsetY: number,
): VectorOrigination {
  switch (o.kind) {
    case 'rectangle':
      return { kind: 'rectangle', bbox: scaleBBox(o.bbox, xScale, yScale, offsetX, offsetY) };
    case 'roundedRect':
      return {
        kind: 'roundedRect',
        bbox: scaleBBox(o.bbox, xScale, yScale, offsetX, offsetY),
        radii: o.radii.map(r => r * uniformEffectScale(xScale, yScale)) as unknown as readonly [number, number, number, number],
      };
    case 'ellipse':
      return { kind: 'ellipse', bbox: scaleBBox(o.bbox, xScale, yScale, offsetX, offsetY) };
    case 'line':
      return {
        kind: 'line',
        from: { x: o.from.x * xScale + offsetX, y: o.from.y * yScale + offsetY },
        to: { x: o.to.x * xScale + offsetX, y: o.to.y * yScale + offsetY },
      };
    case 'path':
      return o;
  }
}

export function scalePaths(
  paths: readonly BezierPath[],
  xScale: number,
  yScale: number,
  offsetX: number,
  offsetY: number,
): readonly BezierPath[] {
  return paths.map(p => ({
    ...p,
    knots: p.knots.map(k => ({
      ...k,
      points: k.points.map((v, i) => (i % 2 === 0 ? v * yScale + offsetY : v * xScale + offsetX)),
    })),
  }));
}

/**
 * Спутники вектора лежат в РАЗНЫХ кадрах, и это единственное место, где закон записан целиком.
 *
 * `origination` — кадр СТРАНИЦЫ. Парсер кладёт туда `keyOriginShapeBBox` в координатах документа,
 * там же, где `rect`; `line.from/to` — та же диагональ того же bbox, а не отдельное поле. Ни один
 * рендерер `bbox` не читает (фигуру строит из рамки), но разъехаться с рамкой, рядом с которой он
 * лежит, поле не должно, — значит перенос ему нужен весь.
 *
 * `paths` — кадр РАМКИ. Парсер вычитает из узлов начало слоя (`normalizePathsToPixelSpace`), и оба
 * рендерера прибавляют начало рамки обратно перед укладкой на холст. Слагаемые переноса увезли бы
 * контур ВТОРОЙ раз; от проекции ему нужен только осевой масштаб — сжавшаяся рамка обязана сжать
 * и фигуру.
 */
export function scaleVectorGeometry(
  geometry: { readonly origination: VectorOrigination; readonly paths: readonly BezierPath[] },
  xScale: number,
  yScale: number,
  offsetX: number,
  offsetY: number,
): { origination: VectorOrigination; paths: readonly BezierPath[] } {
  return {
    origination: scaleOrigination(geometry.origination, xScale, yScale, offsetX, offsetY),
    paths: scalePaths(geometry.paths, xScale, yScale, 0, 0),
  };
}

/**
 * Из видов заливки абсолютные пиксели несёт только радиальная рампа: `center` и `radius` рендер берёт как
 * есть, а ось линейной строит из рамки сам. `center` лежит в кадре РАМКИ, как `paths` (оба рендерера
 * прибавляют начало рамки), — от проекции ему нужен только осевой масштаб, перенос увёз бы его второй
 * раз. `radius` — скаляр без оси и едет законом `uniformEffectScale`. Рамка нулевого, отрицательного
 * или нечислового масштаба рампу не трогает целиком, вместе с `center`. Прочие виды возвращаются тем же
 * объектом.
 */
export function scaleVectorFill(fill: VectorFillData | undefined, xScale: number, yScale: number): VectorFillData | undefined {
  if (fill?.kind !== 'radialGradient') return fill;
  if (!isPositiveScale(xScale) || !isPositiveScale(yScale)) return fill;
  return {
    ...fill,
    center: { x: fill.center.x * xScale, y: fill.center.y * yScale },
    radius: fill.radius * uniformEffectScale(xScale, yScale),
  };
}

function isPositiveScale(scale: number): boolean {
  return Number.isFinite(scale) && scale > 0;
}

/**
 * Штрихи `dashPattern` хранятся в px кратно ширине обводки (длины пунктира в Photoshop заданы в ширинах линии),
 * поэтому едут её коэффициентом: иначе толстая линия получила бы прежние короткие штрихи и рисунок пунктира
 * поменялся бы.
 */
export function scaleStroke(s: VectorStrokeData | undefined, xScale: number, yScale: number): VectorStrokeData | undefined {
  if (!s) return undefined;
  const k = uniformEffectScale(xScale, yScale);
  return {
    ...s,
    width: s.width * k,
    ...(s.dashPattern && { dashPattern: s.dashPattern.map(d => d * k) }),
    ...(s.fill && { fill: scaleVectorFill(s.fill, xScale, yScale) }),
  };
}

/**
 * Весь закон вектора за рамкой одним вызовом: контур и габариты (`scaleVectorGeometry`), радиальная заливка
 * (`scaleVectorFill`) и обводка (`scaleStroke`). Перенос (`offsetX`/`offsetY`) — в кадре страницы, его получает
 * только `origination`. Контур и габариты едут каждый своим законом и порознь: линия без контура рисуется прямо по
 * `from`/`to`, поэтому `origination` масштабируется и без `paths`. Отсутствующее поле так и остаётся отсутствующим;
 * тождественное преобразование возвращает тот же объект.
 */
export function scaleVectorParams<T extends Pick<VectorBoxParams, 'origination' | 'paths' | 'fill' | 'stroke'>>(
  params: T,
  xScale: number,
  yScale: number,
  offsetX: number,
  offsetY: number,
): T {
  if (xScale === 1 && yScale === 1 && offsetX === 0 && offsetY === 0) return params;
  return {
    ...params,
    ...(params.origination && { origination: scaleOrigination(params.origination, xScale, yScale, offsetX, offsetY) }),
    ...(params.paths && { paths: scalePaths(params.paths, xScale, yScale, 0, 0) }),
    ...(params.fill && { fill: scaleVectorFill(params.fill, xScale, yScale) }),
    ...(params.stroke && { stroke: scaleStroke(params.stroke, xScale, yScale) }),
  };
}
